<script setup lang="ts">
/**
 * AppToast Component
 *
 * Drop-in PrimeVue Toast wrapper for the app root layout.
 * Renders action buttons when provided via useToaster's `actions` option.
 *
 * Usage:
 *   Place once in your root App.vue template:
 *   <AppToast />
 *
 * Requires:
 *   - PrimeVue plugin registered
 *   - PrimeVue ToastService plugin registered (app.use(ToastService))
 */
import Toast from 'primevue/toast';
import Button from 'primevue/button';
import Icon from '../icons/IconComponent.vue';
import { toastActionsStore, type ToastAction } from '../../composables/useToaster';

defineProps<{
  /** PrimeVue toast group for scoped rendering */
  group?: string;
  /** Position on screen. Defaults to 'top-center'. */
  position?: 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center' | 'center';
}>();

/**
 * Parse actions and detail text from a toast message detail.
 * When actions exist, detail is encoded as "toastId::actualDetail".
 */
function parseMessage(detail: string | undefined): { text: string | undefined; actions: ToastAction[] } {
  if (!detail) return { text: undefined, actions: [] };
  const match = detail.match(/^(toast-\d+)::(.*)/s);
  if (match) {
    const toastId = match[1];
    const actions = toastActionsStore[toastId] ?? [];
    return { text: match[2] || undefined, actions };
  }
  return { text: detail, actions: [] };
}

/**
 * Maps PrimeVue severity to flowbite SVG icon name.
 * These icons are bundled as project assets -- no external font/CDN dependency.
 */
const severityIcon: Record<string, string> = {
  success: 'IconSuccess',
  error: 'IconWarning',
  warn: 'IconWarning',
  info: 'InfoCircle',
};
</script>

<template>
  <Toast
    :group="group"
    :position="position ?? 'top-center'"
  >
    <template #message="slotProps">
      <div class="flex flex-col w-full p-0">
        <div class="flex items-center gap-2">
          <!-- Severity Icon Badge -->
          <span
            class="toast-severity-badge"
            :data-severity="slotProps.message.severity"
          >
            <Icon
              :name="severityIcon[slotProps.message.severity ?? 'info']"
              size-class="h-4 w-4"
              variant="stroke"
              color="currentColor"
              class="toast-severity-icon"
              decorative
            />
          </span>
          <!-- Message -->
          <span class="toast-summary">{{ slotProps.message.summary }}</span>
        </div>
        <p
          v-if="parseMessage(slotProps.message.detail).text"
          class="toast-detail"
        >
          {{ parseMessage(slotProps.message.detail).text }}
        </p>
        <div
          v-if="parseMessage(slotProps.message.detail).actions.length"
          class="flex gap-2 mt-1 ml-12"
        >
          <Button
            v-for="(action, idx) in parseMessage(slotProps.message.detail).actions"
            :key="idx"
            :label="action.label"
            size="small"
            text
            @click="action.onClick"
          />
        </div>
      </div>
    </template>
  </Toast>
</template>
